/* Tansy Active · <tansy-kit>: o comportamento comum dos kits (23/10/2026).
   Usado pela seção da home (sections/tansy-kits.liquid) e pela página do kit (sections/tansy-kit-page.liquid).

   O kit não é um produto: o formulário manda as peças avulsas para POST /cart/add (items[]) e o desconto
   automático da loja faz o preço. As duas faixas chegam por data-pct/data-min e data-pct5/data-min5, escritos a
   partir de snippets/tansy-kit-oferta.liquid (o único lugar onde eles moram).

   Contrato do HTML (tudo dentro do <tansy-kit>):
   - [data-peca] uma por peça, com <script type="application/json" data-variantes> ([{id, cor, tam, ok, preco}]);
     a cor vem de [data-cor] (rádios, select ou campo escondido) e, num kit misto, o tamanho de [data-tamanho]
     dentro da peça. [data-aviso] é o aviso de esgotado: com data-texto, o script escreve e apaga o texto (região
     ao vivo, para o leitor de tela anunciar); sem ele, só mostra e esconde. [data-cor-nome] mostra a cor escolhida.
     [data-pede-tamanho] dentro da peça (kit misto): o pedido de tamanho daquela peça.
     Na página: <script data-fotos> ({cor: {g, gs, p, alt}}) e [data-mini] (a foto pequena da peça).
     Na home: [data-id] é o campo escondido items[n][id] de cada peça.
   - [data-tamanho] fora das peças: um tamanho para o kit inteiro; [data-tamanho-nome] mostra o escolhido e
     [data-pede-tamanho] fora das peças pede o tamanho do kit.
   - [data-botao] o botão; com [data-rotulo] dentro, o texto acompanha a escolha (pede o tamanho ou mostra o total).
     Na página ele vem desligado do servidor (sem os items[] o pedido sairia vazio) e o script liga.
   - [data-itens] (página): o script escreve aqui os campos items[] do formulário, juntando peças iguais numa
     linha só com a quantidade somada. [data-total], [data-por-peca] e [data-valor]: a conta ao vivo.
   - [data-galeria] (página): [data-trilho] com os [data-slide], miniaturas [data-ir="n"], setas
     [data-anterior] e [data-proximo]; a foto da peça n é [data-peca-foto="n"] e a miniatura [data-peca-mini="n"].
     Ao trocar a cor de uma peça, a foto dela troca e a galeria vai até ela. */
if (!customElements.get('tansy-kit')) {
  const lerJson = (el, padrao) => {
    try {
      return JSON.parse(el?.textContent || '') ?? padrao;
    } catch (e) {
      return padrao;
    }
  };

  // Valor de um grupo de controles: o rádio marcado, ou o select / campo escondido
  const lerValor = (campos) => {
    const marcado = campos.find((c) => c.type === 'radio' && c.checked);
    if (marcado) return marcado.value;
    const outro = campos.find((c) => c.type !== 'radio');
    return outro ? outro.value : '';
  };

  customElements.define(
    'tansy-kit',
    class extends HTMLElement {
      connectedCallback() {
        if (this.pronto) return;
        this.pronto = true;
        this.pct = Number(this.dataset.pct) || 0;
        this.min = Number(this.dataset.min) || 2;
        this.pct5 = Number(this.dataset.pct5) || 0;
        this.min5 = Number(this.dataset.min5) || Infinity;
        this.formatoMoeda = this.dataset.moeda || '${{amount}}';
        this.botao = this.querySelector('[data-botao]');
        this.rotulo = this.querySelector('[data-rotulo]');
        this.itens = this.querySelector('[data-itens]');
        this.form = this.querySelector('form[action*="/cart/add"]');
        this.pedeTamanho = [...this.querySelectorAll('[data-pede-tamanho]')].find((x) => !x.closest('[data-peca]'));
        this.tamanhosKit = [...this.querySelectorAll('[data-tamanho]')].filter((c) => !c.closest('[data-peca]'));
        this.nomeTamanhoKit = this.querySelector('[data-tamanho-nome]');

        this.pecas = [...this.querySelectorAll('[data-peca]')].map((el) => {
          const variantes = lerJson(el.querySelector('[data-variantes]'), []);
          return {
            el,
            variantes,
            fotos: lerJson(el.querySelector('[data-fotos]'), {}),
            cores: [...el.querySelectorAll('[data-cor]')],
            tamanhos: [...el.querySelectorAll('[data-tamanho]')],
            precisaTamanho: variantes.some((x) => x.tam),
            id: el.querySelector('[data-id]'),
            aviso: el.querySelector('[data-aviso]'),
            pede: el.querySelector('[data-pede-tamanho]'),
            nomeCor: el.querySelector('[data-cor-nome]'),
            nomeTamanho: el.querySelector('[data-tamanho-nome]'),
            mini: el.querySelector('[data-mini]'),
          };
        });

        this.prepararGaleria();
        this.addEventListener('change', (e) => this.atualizar(e.target));
        if (this.rotulo && this.form) this.form.addEventListener('submit', (e) => this.enviar(e));
        this.atualizar(null);
      }

      dinheiro(centavos) {
        const n = Math.max(0, Math.round(centavos)) / 100;
        const com = (sep, dec, casas) => {
          const [int, frac] = n.toFixed(casas).split('.');
          return int.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (frac ? dec + frac : '');
        };
        return this.formatoMoeda.replace(/\{\{\s*(\w+)\s*\}\}/, (_, tipo) => {
          if (tipo === 'amount_no_decimals') return com(',', '.', 0);
          if (tipo === 'amount_with_comma_separator') return com('.', ',', 2);
          if (tipo === 'amount_no_decimals_with_comma_separator') return com('.', ',', 0);
          return com(',', '.', 2);
        });
      }

      // Variante de cada peça para a cor e o tamanho escolhidos. Sem tamanho ainda: a primeira disponível na
      // cor (só para a foto e para a conta; o formulário não sai sem tamanho).
      escolher(p, tamKit) {
        const cor = lerValor(p.cores);
        const tam = p.tamanhos.length ? lerValor(p.tamanhos) : tamKit;
        const daCor = p.variantes.filter((x) => (x.cor ?? '') === cor);
        const faltaTamanho = p.precisaTamanho && !tam;
        let v;
        if (p.precisaTamanho && tam) v = daCor.find((x) => x.tam === tam);
        else v = daCor.find((x) => x.ok) || daCor[0];
        return { cor, tam, v, faltaTamanho, ok: Boolean(v && v.ok) };
      }

      atualizar(origem) {
        const tamKit = lerValor(this.tamanhosKit);
        if (this.nomeTamanhoKit) this.nomeTamanhoKit.textContent = tamKit;
        let faltaTamanho = false;
        let esgotado = false;
        const escolhidas = [];
        const avisosAbertos = [];

        this.pecas.forEach((p, i) => {
          const e = this.escolher(p, tamKit);
          if (e.faltaTamanho) faltaTamanho = true;
          if (e.v && p.id) p.id.value = e.v.id;
          const avisar = !e.faltaTamanho && !e.ok;
          if (avisar) esgotado = true;
          if (p.aviso) {
            if (p.aviso.dataset.texto !== undefined) {
              const texto = avisar ? p.aviso.dataset.texto : '';
              if (p.aviso.textContent !== texto) p.aviso.textContent = texto;
            } else {
              p.aviso.hidden = !avisar;
            }
            if (avisar && p.aviso.id) avisosAbertos.push(p.aviso.id);
          }
          if (p.pede && !e.faltaTamanho) p.pede.hidden = true;
          if (p.nomeCor) p.nomeCor.textContent = e.cor;
          if (p.nomeTamanho) p.nomeTamanho.textContent = p.tamanhos.length ? e.tam : '';
          p.el.toggleAttribute('data-esgotada', avisar);
          escolhidas.push(e.v);

          // Cores desta peça: risca a que não tem estoque no tamanho escolhido (sem tamanho: em nenhum)
          p.cores.forEach((c) => {
            if (c.type !== 'radio') return;
            const tem = p.variantes.some(
              (y) => (y.cor ?? '') === c.value && y.ok && (!p.precisaTamanho || !e.tam || y.tam === e.tam)
            );
            c.closest('.mm-opt__item')?.classList.toggle('mm-opt__item--out', !tem);
          });

          // Tamanho desta peça (kit misto): risca o que não tem na cor escolhida
          p.tamanhos.forEach((c) => {
            const x = p.variantes.find((y) => (y.cor ?? '') === e.cor && y.tam === c.value);
            c.closest('.mm-opt__item')?.classList.toggle('mm-opt__item--out', !(x && x.ok));
          });

          const mudouCor = origem && p.cores.includes(origem);
          if (mudouCor || origem === null) this.trocarFoto(i, e.cor, mudouCor);
        });

        // Tamanho do kit: risca o tamanho em que alguma peça, na cor escolhida, não tem estoque
        this.tamanhosKit.forEach((c) => {
          const falta = this.pecas.some((p) => {
            if (!p.precisaTamanho) return false;
            const cor = lerValor(p.cores);
            const x = p.variantes.find((y) => (y.cor ?? '') === cor && y.tam === c.value);
            return !(x && x.ok);
          });
          c.closest('.mm-opt__item')?.classList.toggle('mm-opt__item--out', falta);
        });

        if (!faltaTamanho && this.pedeTamanho) this.pedeTamanho.hidden = true;
        this.faltaTamanho = faltaTamanho;
        this.escreverItens(escolhidas);
        const conta = this.contar(escolhidas);

        if (this.rotulo) {
          // Página do kit: sem tamanho o botão pede o tamanho; com uma peça esgotada ele fica desligado
          if (this.botao) {
            this.botao.disabled = esgotado && !faltaTamanho;
            // Desligado por peça esgotada: o botão aponta para os avisos
            if (this.botao.disabled && avisosAbertos.length) {
              this.botao.setAttribute('aria-describedby', avisosAbertos.join(' '));
            } else {
              this.botao.removeAttribute('aria-describedby');
            }
          }
          this.rotulo.textContent = faltaTamanho
            ? this.botao.dataset.textoTamanho
            : `${this.botao.dataset.textoAdd} · ${this.dinheiro(conta.total)}`;
        } else if (this.botao) {
          // Home: o tamanho é campo obrigatório; o botão só desliga com uma peça esgotada no tamanho escolhido
          this.botao.disabled = esgotado;
        }
      }

      // A mesma conta do carrinho (snippets/tansy-kit-conta.liquid): a faixa aplicável desconta uma vez
      // sobre a soma, arredondando para baixo ao centavo.
      contar(escolhidas) {
        const pecas = escolhidas.filter(Boolean);
        const n = pecas.length;
        const soma = pecas.reduce((s, v) => s + (v.preco || 0), 0);
        const pct = n >= this.min5 ? this.pct5 : n >= this.min ? this.pct : 0;
        const desconto = Math.floor((soma * pct) / 100);
        const total = soma - desconto;
        const set = (sel, txt) => this.querySelectorAll(sel).forEach((el) => (el.textContent = txt));
        set('[data-total]', this.dinheiro(total));
        set('[data-valor]', this.dinheiro(soma));
        if (n) set('[data-por-peca]', this.dinheiro(total / n));
        return { total, soma, n };
      }

      // Os campos items[] do formulário (página): uma linha por variante, com a quantidade somada
      escreverItens(escolhidas) {
        if (!this.itens) return;
        const qtd = new Map();
        escolhidas.forEach((v) => v && qtd.set(v.id, (qtd.get(v.id) || 0) + 1));
        const campos = [];
        let n = 0;
        qtd.forEach((q, id) => {
          campos.push(`<input type="hidden" name="items[${n}][id]" value="${Number(id)}">`);
          campos.push(`<input type="hidden" name="items[${n}][quantity]" value="${q}">`);
          n += 1;
        });
        this.itens.innerHTML = campos.join('');
      }

      enviar(e) {
        this.atualizar(null);
        if (this.faltaTamanho) {
          e.preventDefault();
          if (this.pedeTamanho) this.pedeTamanho.hidden = false;
          // Kit misto: cada peça sem tamanho mostra o pedido dela
          const semTamanho = this.tamanhosKit.length
            ? []
            : this.pecas.filter((p) => p.precisaTamanho && p.tamanhos.length && !lerValor(p.tamanhos));
          semTamanho.forEach((p) => {
            if (p.pede) p.pede.hidden = false;
          });
          const grupo = this.tamanhosKit.length ? this.tamanhosKit : semTamanho[0]?.tamanhos || [];
          const alvo = grupo[0];
          if (alvo) {
            alvo.closest('fieldset')?.scrollIntoView({ block: 'center', behavior: this.suave() });
            alvo.focus({ preventScroll: true });
          }
          return;
        }
        if (this.botao?.disabled) e.preventDefault();
      }

      suave() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      /* ---------- Galeria (só na página) ---------- */

      prepararGaleria() {
        this.galeria = this.querySelector('[data-galeria]');
        if (!this.galeria) return;
        this.trilho = this.galeria.querySelector('[data-trilho]');
        this.slides = [...this.galeria.querySelectorAll('[data-slide]')];
        this.miniaturas = [...this.galeria.querySelectorAll('[data-ir]')];
        this.anterior = this.galeria.querySelector('[data-anterior]');
        this.proximo = this.galeria.querySelector('[data-proximo]');
        this.atual = 0;
        this.miniaturas.forEach((m) => m.addEventListener('click', () => this.irPara(Number(m.dataset.ir))));
        this.anterior?.addEventListener('click', () => this.irPara(this.atual - 1));
        this.proximo?.addEventListener('click', () => this.irPara(this.atual + 1));
        let quadro = 0;
        this.trilho.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(quadro);
            quadro = requestAnimationFrame(() => this.marcar(Math.round(this.trilho.scrollLeft / this.trilho.clientWidth)));
          },
          { passive: true }
        );
        this.trilho.addEventListener('keydown', (e) => {
          const passo = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!passo) return;
          e.preventDefault();
          this.irPara(this.atual + passo);
        });
        this.marcar(0);
      }

      irPara(n) {
        if (!this.trilho) return;
        const k = Math.max(0, Math.min(this.slides.length - 1, n));
        this.trilho.scrollTo({ left: this.slides[k].offsetLeft, behavior: this.suave() });
        this.marcar(k);
      }

      marcar(k) {
        if (!this.slides) return;
        this.atual = Math.max(0, Math.min(this.slides.length - 1, k));
        this.miniaturas.forEach((m) => {
          const ativa = Number(m.dataset.ir) === this.atual;
          if (ativa) m.setAttribute('aria-current', 'true');
          else m.removeAttribute('aria-current');
        });
        // Coluna de miniaturas que rola (kit de 5 peças no computador): a miniatura da foto à mostra fica à vista
        const ativa = this.miniaturas.find((m) => m.hasAttribute('aria-current'));
        const lista = ativa?.parentElement;
        if (lista && lista.scrollHeight > lista.clientHeight + 1) {
          lista.scrollTo({ top: ativa.offsetTop - (lista.clientHeight - ativa.offsetHeight) / 2, behavior: this.suave() });
        }
        if (this.anterior) this.anterior.disabled = this.atual === 0;
        if (this.proximo) this.proximo.disabled = this.atual === this.slides.length - 1;
      }

      // Troca a foto da peça i para a cor escolhida; ir = true leva a galeria até ela
      trocarFoto(i, cor, ir) {
        const p = this.pecas[i];
        const f = p.fotos[cor];
        if (f) {
          this.querySelectorAll(`[data-peca-foto="${i}"]`).forEach((img) => {
            if (img.getAttribute('src') === f.g) return;
            img.srcset = f.gs || '';
            img.src = f.g;
            img.alt = f.alt || '';
          });
          const pequenas = [...this.querySelectorAll(`[data-peca-mini="${i}"]`)];
          if (p.mini) pequenas.push(p.mini);
          pequenas.forEach((img) => {
            if (img.getAttribute('src') !== f.p) img.src = f.p;
          });
        }
        if (ir && this.galeria) {
          const k = this.slides.findIndex((s) => s.querySelector(`[data-peca-foto="${i}"]`));
          if (k >= 0) this.irPara(k);
        }
      }
    }
  );
}
